/**
 * API методы для работы с файлами
 */

import { AxiosInstance } from 'axios';
import { FileUploadResponse } from '@/types/api.types';

/**
 * Files API Service
 */
export class FilesApiService {
  constructor(private client: AxiosInstance) {}

  /**
   * Загружает файл на сервер
   * 
   * @description Загружает файл через multipart/form-data
   * 
   * @param file - файл для загрузки
   * @param userId - ID пользователя
   * @param onProgress - колбек прогресса (опционально)
   * @returns информация о загруженном файле
   */
  async uploadFile(
    file: File,
    userId: string,
    onProgress?: (progress: number) => void
  ): Promise<FileUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('user', userId);

    const response = await this.client.post<FileUploadResponse>('/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      },
    });

    return response.data;
  }

  /**
   * Получает URL для preview файла
   * 
   * @param fileId - ID файла
   * @param asAttachment - скачать как вложение
   * @returns URL файла
   */
  getFilePreviewUrl(fileId: string, asAttachment: boolean = false): string {
    const baseURL = this.client.defaults.baseURL || '';
    const params = asAttachment ? '?as_attachment=true' : '';
    return `${baseURL}/files/${fileId}/preview${params}`;
  }

  /**
   * Загружает файл как Blob
   * 
   * @param fileId - ID файла
   * @returns Blob файла
   */
  async downloadFile(fileId: string): Promise<Blob> {
    const response = await this.client.get(`/files/${fileId}/preview`, {
      responseType: 'blob',
    });
    return response.data;
  }
}

